import * as React from "react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

export interface NumberInputProps
  extends Omit<React.ComponentProps<"input">, "type" | "onChange" | "value"> {
  /** The current value */
  value?: number | string;
  /** Called when the value changes */
  onChange?: (value: number | string) => void;
  /** Called when the input loses focus */
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Step increment for the input (default: 0.01) */
  step?: string | number;
  /** Minimum value */
  min?: number;
  /** Maximum value */
  max?: number;
  /** Default value when field is empty on blur (default: 0) */
  defaultOnBlur?: number;
  /** Whether to allow the field to remain empty (no default on blur) */
  allowEmpty?: boolean;
}

/**
 * A standardized number input component that provides:
 * - No spinner arrows (cleaner appearance)
 * - Auto-select on focus (easy value replacement)
 * - Allow empty string during editing (natural backspace/delete behavior)
 * - Default to 0 on blur (prevents invalid state on form submission)
 *
 * @example
 * // With react-hook-form
 * <FormField
 *   control={form.control}
 *   name="amount"
 *   render={({ field, fieldState }) => (
 *     <FormItem>
 *       <FormLabel>Amount</FormLabel>
 *       <FormControl>
 *         <NumberInput
 *           {...field}
 *           className={fieldState.error ? "border-destructive" : ""}
 *         />
 *       </FormControl>
 *     </FormItem>
 *   )}
 * />
 *
 * @example
 * // With local state
 * const [amount, setAmount] = useState<number | string>(0);
 * <NumberInput value={amount} onChange={setAmount} />
 */
const NumberInput = React.forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      className,
      value,
      onChange,
      onBlur,
      onFocus,
      step = "0.01",
      min,
      max,
      defaultOnBlur = 0,
      allowEmpty = false,
      ...props
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;
      if (inputValue === "") {
        onChange?.("");
      } else {
        onChange?.(parseFloat(inputValue) || 0);
      }
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      // Call the original onBlur first (for react-hook-form)
      onBlur?.(e);

      // Default to specified value if empty and not allowing empty
      if (!allowEmpty && e.target.value === "") {
        onChange?.(defaultOnBlur);
      }
    };

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      // Select all text on focus for easy replacement
      e.target.select();
      onFocus?.(e);
    };

    return (
      <Input
        type="number"
        step={step}
        min={min}
        max={max}
        className={cn(
          "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
          className
        )}
        value={value}
        onChange={handleChange}
        onBlur={handleBlur}
        onFocus={handleFocus}
        ref={ref}
        {...props}
      />
    );
  }
);

NumberInput.displayName = "NumberInput";

export { NumberInput };
